import { useEffect } from 'react';

interface ShortcutOptions {
  /** Require Cmd (mac) / Ctrl (win). */
  meta?: boolean;
  /** Ignore when focus is in an input/textarea/contenteditable. */
  ignoreInInputs?: boolean;
}

/**
 * Registers a global keyboard shortcut. `key` is compared case-insensitively.
 * Example: useKeyboardShortcut('k', open, { meta: true }) for Cmd/Ctrl-K.
 */
export function useKeyboardShortcut(
  key: string,
  handler: (e: KeyboardEvent) => void,
  options: ShortcutOptions = {},
): void {
  const { meta = false, ignoreInInputs = true } = options;

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      const inField =
        e.target instanceof HTMLElement &&
        (e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable);
      if (ignoreInInputs && inField && !meta) return;
      const metaOk = meta ? e.metaKey || e.ctrlKey : true;
      if (metaOk && e.key.toLowerCase() === key.toLowerCase()) {
        handler(e);
      }
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [key, handler, meta, ignoreInInputs]);
}
